import React, { useState, useEffect } from 'react';
import { Users, Search, Download, RefreshCw, CheckCircle, XCircle, Crown, Shield, User, Save, Settings, Trash2 } from 'lucide-react';
import { getAllUsers, saveSubscription, saveUserProfile, deleteUserProfile } from './services/firestoreService';

const TIER_COLORS = {
  free:    'bg-gray-100 text-gray-700',
  Pro:     'bg-blue-100 text-blue-700',
  Elite: 'bg-purple-100 text-purple-700'
};

const PLAN_OPTIONS = [
  { value: 'free',  label: 'Free',  planName: 'Free' },
  { value: 'pro',   label: 'Pro',   planName: 'Pro' },
  { value: 'elite', label: 'Elite', planName: 'Elite' },
];

const ADMIN_EMAIL = 'krishautomation.services@gmail.com';

const AdminPanel = ({ currentUserEmail }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');

  const [adminSearch, setAdminSearch] = useState('');

  // User management state
  const [userEdits, setUserEdits] = useState({});   // { [uid]: { planId, endDate, mobile } }
  const [savingUsers, setSavingUsers] = useState({}); // { [uid]: bool }
  const [saveMessages, setSaveMessages] = useState({}); // { [uid]: { type, text } }

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAllUsers();
      setUsers(data);
      // Initialize edit state from current subscription data
      const edits = {};
      data.forEach(u => {
        const tier = (u.subscriptionTier || 'free').toLowerCase();
        const planId = ['pro', 'elite'].includes(tier) ? tier : 'free';
        const endDate = u.subscriptionEndDate
          ? u.subscriptionEndDate.split('T')[0]
          : '';
        edits[u.id] = { planId, endDate, mobile: u.mobile || '' };
      });
      setUserEdits(edits);
    } catch (err) {
      setError('Failed to load users. Check Firestore rules.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (uid, field, value) => {
    setUserEdits(prev => ({ ...prev, [uid]: { ...prev[uid], [field]: value } }));
  };

  const handleSaveSubscription = async (user) => {
    const edit = userEdits[user.id] || {};
    const planId = edit.planId || 'free';
    const endDate = edit.endDate;
    const mobile = (edit.mobile || '').trim();

    if (planId !== 'free' && !endDate) {
      setSaveMessages(prev => ({ ...prev, [user.id]: { type: 'error', text: 'Please set an end date.' } }));
      return;
    }

    setSavingUsers(prev => ({ ...prev, [user.id]: true }));
    setSaveMessages(prev => ({ ...prev, [user.id]: null }));

    try {
      const today = new Date().toISOString().split('T')[0];
      const planOption = PLAN_OPTIONS.find(p => p.value === planId) || PLAN_OPTIONS[0];

      const saveOps = [];

      if (planId === 'free') {
        saveOps.push(saveSubscription(user.id, {
          planId: 'free',
          planName: 'Free',
          status: 'cancelled',
          startDate: today,
          endDate: today,
          adminUpdated: true,
          adminUpdatedAt: new Date().toISOString(),
        }));
      } else {
        saveOps.push(saveSubscription(user.id, {
          planId,
          planName: planOption.planName,
          status: 'active',
          startDate: today,
          endDate: endDate,
          adminUpdated: true,
          adminUpdatedAt: new Date().toISOString(),
        }));
      }

      if (mobile !== (user.mobile || '')) {
        saveOps.push(saveUserProfile(user.id, { mobile }));
      }

      await Promise.all(saveOps);

      // Update local user list to reflect change
      setUsers(prev => prev.map(u =>
        u.id === user.id
          ? { ...u, subscriptionTier: planOption.planName, subscriptionEndDate: planId !== 'free' ? endDate : null, mobile }
          : u
      ));
      setSaveMessages(prev => ({ ...prev, [user.id]: { type: 'success', text: 'Saved!' } }));
      setTimeout(() => setSaveMessages(prev => ({ ...prev, [user.id]: null })), 3000);
    } catch (err) {
      setSaveMessages(prev => ({ ...prev, [user.id]: { type: 'error', text: 'Save failed.' } }));
    } finally {
      setSavingUsers(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const handleDeleteBlankUser = async (userId) => {
    if (!window.confirm('Delete this blank user entry? This cannot be undone.')) return;
    try {
      await deleteUserProfile(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // --- Filtered list ---
  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.mobile || '').includes(q);

    const tier = (u.subscriptionTier || 'free').toLowerCase();
    const matchTier =
      tierFilter === 'all' ||
      (tierFilter === 'paid' ? tier !== 'free' : tier === tierFilter.toLowerCase());

    const matchVerified =
      verifiedFilter === 'all' ||
      (verifiedFilter === 'verified' ? u.mobileVerified === true : u.mobileVerified !== true);

    return matchSearch && matchTier && matchVerified;
  });

  // --- Stats ---
  const totalUsers  = users.length;
  const paidUsers   = users.filter(u => (u.subscriptionTier || 'free').toLowerCase() !== 'free').length;
  const freeUsers   = totalUsers - paidUsers;
  const verifiedCt  = users.filter(u => u.mobileVerified === true).length;
  const conversion  = totalUsers > 0 ? ((paidUsers / totalUsers) * 100).toFixed(1) : '0.0';

  // --- CSV Export ---
  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Mobile', 'Subscription', 'Signup Date', 'Verified'];
    const rows = filtered.map(u => [
      u.name || '',
      u.email || '',
      u.mobile || '',
      u.subscriptionTier || 'free',
      u.signupDate?.toDate ? u.signupDate.toDate().toLocaleDateString('en-IN') : (u.signupDate || u.createdAt || ''),
      u.mobileVerified ? 'Yes' : 'No'
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `tradefix-users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (val) => {
    if (!val) return '—';
    if (val?.toDate) return val.toDate().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    if (typeof val === 'string') return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    return '—';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Marketing Dashboard</h2>
          <p className="text-gray-500 text-sm mt-0.5">User base overview &amp; contact data</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            onClick={exportCSV}
            disabled={loading || filtered.length === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Download size={15} />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Users size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Crown size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">Paid Users</p>
              <p className="text-2xl font-bold text-gray-900">{paidUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <User size={20} className="text-gray-600" />
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">Free Users</p>
              <p className="text-2xl font-bold text-gray-900">{freeUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Shield size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">Conversion</p>
              <p className="text-2xl font-bold text-gray-900">{conversion}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters + Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email or mobile..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Tier filter */}
          <div className="flex items-center space-x-1.5">
            {['all', 'free', 'paid', 'Pro', 'Elite'].map(t => (
              <button
                key={t}
                onClick={() => setTierFilter(t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors capitalize ${
                  tierFilter === t
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t === 'all' ? 'All Tiers' : t}
              </button>
            ))}
          </div>

          {/* Verified filter */}
          <div className="flex items-center space-x-1.5">
            {[
              { key: 'all',        label: 'All' },
              { key: 'verified',   label: 'Verified' },
              { key: 'unverified', label: 'Unverified' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setVerifiedFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  verifiedFilter === f.key
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-2">
          Showing {filtered.length} of {totalUsers} users &bull; {verifiedCt} mobile verified
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500">
            <RefreshCw size={32} className="mx-auto mb-3 animate-spin text-blue-400" />
            <p>Loading users...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <Users size={40} className="mx-auto mb-3 opacity-40" />
            <p>No users match the current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">#</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Name</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Email</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Mobile</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Subscription</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Signup Date</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Verified</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user, idx) => (
                  <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3.5 text-gray-400">{idx + 1}</td>
                    <td className="px-5 py-3.5 font-medium text-gray-900">{user.name || '—'}</td>
                    <td className="px-5 py-3.5 text-gray-600">{user.email || '—'}</td>
                    <td className="px-5 py-3.5 text-gray-600 font-mono text-xs">
                      {user.mobile
                        ? user.mobile.replace(/^\+91(\d{5})(\d{5})$/, '+91-$1-$2')
                        : '—'}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${TIER_COLORS[user.subscriptionTier] || TIER_COLORS.free}`}>
                        {user.subscriptionTier || 'free'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-500 text-xs">{formatDate(user.signupDate || user.createdAt)}</td>
                    <td className="px-5 py-3.5">
                      {user.mobileVerified ? (
                        <span className="flex items-center space-x-1 text-green-600 text-xs font-medium">
                          <CheckCircle size={14} />
                          <span>Verified</span>
                        </span>
                      ) : (
                        <span className="flex items-center space-x-1 text-gray-400 text-xs">
                          <XCircle size={14} />
                          <span>Pending</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── User Management (admin only) ── */}
      {currentUserEmail === ADMIN_EMAIL && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center space-x-2">
            <Settings size={18} className="text-purple-600" />
            <h3 className="text-base font-semibold text-gray-900">User Management</h3>
            <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">Admin Only</span>
          </div>

          {/* Search bar */}
          <div className="px-5 py-3 border-b border-gray-100">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input
                type="text"
                value={adminSearch}
                onChange={e => setAdminSearch(e.target.value)}
                placeholder="Search by email or mobile..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Loading...</div>
          ) : users.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">No users found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Email</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Mobile Number</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Current Plan</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Expiry Date</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">New Plan</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">New End Date</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users
                    .filter(u => {
                      const q = adminSearch.toLowerCase().trim();
                      if (!q) return true;
                      return (u.email || '').toLowerCase().includes(q) || (u.mobile || '').includes(q);
                    })
                    .map(user => {
                    const edit = userEdits[user.id] || { planId: 'free', endDate: '', mobile: '' };
                    const isSaving = savingUsers[user.id];
                    const msg = saveMessages[user.id];
                    return (
                      <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3 text-gray-700 text-xs">{user.email || '—'}</td>
                        <td className="px-5 py-3">
                          <input
                            type="tel"
                            value={edit.mobile}
                            onChange={e => handleEditChange(user.id, 'mobile', e.target.value)}
                            placeholder="e.g. +919876543210"
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono w-36 focus:outline-none focus:ring-2 focus:ring-purple-400"
                          />
                        </td>
                        <td className="px-5 py-3">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${TIER_COLORS[user.subscriptionTier] || TIER_COLORS.free}`}>
                            {user.subscriptionTier || 'free'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-gray-500 text-xs">
                          {user.subscriptionEndDate ? user.subscriptionEndDate.split('T')[0] : '—'}
                        </td>
                        <td className="px-5 py-3">
                          <select
                            value={edit.planId}
                            onChange={e => handleEditChange(user.id, 'planId', e.target.value)}
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white"
                          >
                            {PLAN_OPTIONS.map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-5 py-3">
                          <input
                            type="date"
                            value={edit.endDate}
                            disabled={edit.planId === 'free'}
                            onChange={e => handleEditChange(user.id, 'endDate', e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center space-x-2">
                            {!user.name && !user.email ? (
                              <button
                                onClick={() => handleDeleteBlankUser(user.id)}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors"
                                title="Delete blank entry"
                              >
                                <Trash2 size={12} />
                                <span>Delete</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleSaveSubscription(user)}
                                disabled={isSaving}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                              >
                                {isSaving ? (
                                  <RefreshCw size={12} className="animate-spin" />
                                ) : (
                                  <Save size={12} />
                                )}
                                <span>{isSaving ? 'Saving...' : 'Save'}</span>
                              </button>
                            )}
                            {msg && (
                              <span className={`text-xs font-medium ${msg.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                                {msg.text}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
